import { makeStyles } from "@material-ui/core/styles";
import { AppList } from "components/list/AppList";
import React, { useEffect } from "react";
import {
	Loading,
	TextField,
	useGetList,
	useRefresh,
	useTranslate,
} from "react-admin";

import { Datagrid } from "../../../components/CustomizableDataGrid/";
import { DateFormatField, DateTimeField } from "../../../components/fields/DateFormatField";
import { OSFCUserSelectField } from "../../../components/fields/OSFCUserSelect";
import { PriorityField } from "../../../components/fields/PriorityField";
import { UserFullNameReferenceField } from "../../../components/fields/UserFullNameReferenceField";
import LabelWithTooltip from "../../../components/LabelWithTooltip";
import { ScrollingWrapper } from "../../../components/ScrollingWrapper";
import { SearchResultDocumentsRowExpand } from "../../../components/SearchResultRowExpand";
import GridUserDataProvider, {
	useGridUserDataContext,
} from "../../../contexts/GridUserDataProvider";
import { AppManagerRoles, EntityType, OSFCUserRoles } from "../../../entities";
import { isInArray } from "../../../utils/general";
import { useAsyncColumnsStorage } from "../../../utils/hooks/useAsyncColumnsStorage";
import { useIsSearchMode } from "../../../utils/hooks/useIsSearchMode";
import { ActionButtonsField, EntitiesActions } from "./Actions";
import {
	MatchCompanyField,
	MatchInternalNotesField,
	MatchNotesField,
	MatchServiceField,
	MatchStatusField,
	OutgoingTripNumberOrIsRecurringField,
} from "./Fields";
import { EntitiesFilter, filterDefaultValues } from "./Filters";
import { useGridUserDataGlobalFiltersContext } from "contexts/GridUserDataGlobalFiltersProvider";

const defaultSort = { field: "id", order: "DESC" };

const BG_COLOR_WHITE = "#ffffff";
const BG_COLOR_GREEN = "#b7ff7d";
const BG_COLOR_RED = "#ff8c8c";

const useStyles = makeStyles({
	list: {
		noResults: {
			backgroundColor: "red",
			padding: 100,
		},
		actions: {
			backgroundColor: "blue",
		},
	},
	expandIcon: {
		padding: "2px",
	},
});

const useCellStyles = makeStyles({
	cellGreen: {
		backgroundColor: BG_COLOR_GREEN, // green
		outline: "1px solid white",
		".order-cc-late": {
			backgroundColor: BG_COLOR_RED,
		},
		"order-car-detected-eta": {
			borderColor: BG_COLOR_GREEN,
		},
	},
	cellRed: {
		backgroundColor: BG_COLOR_RED, // red
		outline: "1px solid white",
		".order-cc-late": {
			backgroundColor: BG_COLOR_RED,
		},
		"order-car-detected-eta": {
			borderColor: BG_COLOR_GREEN,
		},
	},
	noPaddingButton: {
		padding: "0",

		"&>span>button": {
			backgroundColor: BG_COLOR_WHITE,
			border: "solid 1px white",
			minWidth: "auto",
		},
	},
	noPaddingButtonStart: {
		"&>span>button": {
			borderTopRightRadius: 0,
			borderBottomRightRadius: 0,
			borderRight: "none",
		},
		// '.order-cc-late &>span>button': {
		// 	backgroundColor: BG_COLOR_RED
		// },
	},
	noPaddingButtonEnd: {
		"&>span>button": {
			borderTopLeftRadius: 0,
			borderBottomLeftRadius: 0,
			borderLeft: "none",
		},
		// '.order-cc-late &>span>button': {
		// 	backgroundColor: BG_COLOR_GREEN
		// },
	},
	cellEtaTruck: {
		"&>span": {
			outline: "1px solid white",
		},

		".order-cc-late &>span": {
			backgroundColor: BG_COLOR_RED,
		},
		".order-car-detected-eta &>span": {
			outlineColor: "#1ea814",
		},
	},
	cellEtdTruck: {
		"&>span": {
			outline: "1px solid white",
		},

		".order-cc-late &>span": {
			backgroundColor: BG_COLOR_RED,
		},
		".order-car-detected-etd &>span": {
			outlineColor: "#1ea814",
		},
	},
	cellEtaTime: {
		"&>span": {
			backgroundColor: "1px solid transparent",
		},
		".order-cc-late &>span": {
			backgroundColor: BG_COLOR_RED,
		},
		"order-car-detected-eta &>span": {
			backgroundColor: BG_COLOR_GREEN,
		},
	},
	cellEtdTime: {
		"&>span": {
			backgroundColor: "1px solid transparent",
		},
		".order-cc-late &>span": {
			backgroundColor: BG_COLOR_RED,
		},
		"order-car-detected-etd &>span": {
			backgroundColor: BG_COLOR_GREEN,
		},
	},
	cellEtaTime: {
		"&>span": {
			border: "1px solid transparent",
		},
		".order-cc-late &>span": {
			backgroundColor: BG_COLOR_RED,
		},
		".order-car-detected-eta &>span": {
			backgroundColor: `${BG_COLOR_GREEN} !important`,
		},
	},
	cellEtdTime: {
		"&>span": {
			border: "1px solid transparent",
		},
		".order-cc-late &>span": {
			backgroundColor: BG_COLOR_RED,
		},
		".order-car-detected-etd &>span": {
			backgroundColor: `${BG_COLOR_GREEN} !important`,
		},
	},
	// cellEtaTime: {
	// 	'.order-cc-late &>span': {
	// 		backgroundColor: BG_COLOR_RED,
	// 		color: BG_COLOR_GREEN,
	// 	},
	// },
	// cellEtdTime: {
	// 	'.order-cc-late &>span': {
	// 		backgroundColor: BG_COLOR_GREEN,
	// 		color: BG_COLOR_RED
	// 	},
	// },
});


const prepareEtaArrived = (v) => ({
	port_in: v.port_in,
	eta_time: v.eta_time,
	eta_slot_time: v.eta_slot_time,
});

const prepareEtdArrived = (v) => ({
	port_out: v.port_out,
	etd_time: v.etd_time,
	etd_slot_time: v.etd_slot_time,
});

const _EntitiesList = ({ permissions, ...props }) => {
	const { filters: _currentFilters, sort: currentSort } = useGridUserDataContext();
	const { data: globalFilters = {} } = useGridUserDataGlobalFiltersContext();
	const currentFilters = {..._currentFilters, ...globalFilters};
	const classes = useStyles();
	const cellClasses = useCellStyles();
	const translate = useTranslate();
	const t = (field) => translate(`resources.entities.fields.${field}`);
	const gridLabel = (field) => translate(`osfc.grid-label.${field}`);
	const isSearchMode = useIsSearchMode(currentFilters, props.resource);

	const columnsAsyncStorage = useAsyncColumnsStorage("entities-columns");
	const refresh = useRefresh();

	const { data: orderStatuses } = useGetList("order-statuses");
	const { data: taskStatuses } = useGetList("task-statuses");

	const statusesByEntityType = {
		[EntityType.order]: orderStatuses,
		[EntityType.task]: taskStatuses,
	};

	useEffect(() => {
		if (permissions?.time_refresh_order) {
			const intervalId = setInterval(refresh, permissions.time_refresh_order * 1000);
			return () => clearInterval(intervalId);
		}
	}, [permissions, refresh]);

	if (!permissions || currentFilters === undefined || !columnsAsyncStorage.isLoaded) {
		return <Loading loadingSecondary="" />;
	}

	const orderRowStyle = (record, index) => {
		const statuses = statusesByEntityType[record.type];
		return {
			backgroundColor: statuses[record.status] && statuses[record.status].color,
		};
	};

	const isAppManager = permissions && isInArray(AppManagerRoles, permissions.role);
	const isOSFCUser = permissions && OSFCUserRoles.has(permissions.role);
	return (
		<AppList
			{...props}
			empty={false}
			classes={classes.list}
			exporter={false}
			filters={<EntitiesFilter isSearchMode={isSearchMode} />}
			filter={currentFilters}
			filterDefaultValues={currentFilters}
			bulkActionButtons={false}
			sort={currentSort}
			actions={<EntitiesActions />}
		>
			<ScrollingWrapper>
				<Datagrid
					rowStyle={orderRowStyle}
					storage={columnsAsyncStorage}
					isRowExpandable={() => isSearchMode}
					expand={isSearchMode ? <SearchResultDocumentsRowExpand /> : false}
					expandIconCell={{ padding: "2px" }}
				>
					<ActionButtonsField isSearchMode={isSearchMode} />
					<TextField source="id" />
					<UserFullNameReferenceField
						label="resources.entity-editlog.created_by"
						sortable={false}
					/>
					<PriorityField
						source="priority"
						label={<LabelWithTooltip tooltip={t("priority")} label={gridLabel("priority")} />}
						sortable={false}
					/>
					{/* TODO: MAke the same view as in Order grid */}
					{/* <TextEditableField editable={isAppManager} source="rank" />
					<TextField source="rank_auto" /> */}
					<TextField source="title" label="resources.tasks.fields.title" />
					<TextField
						source="terminal_name"
						label="resources.entities.fields.terminal"
						sortable={true}
					/>
					{/* TODO: Ask Erik if we need t ouse srat_plan_datetime and deadline_date_time? */}
					{/* <DateFormatField
						source="multi_eta_date"
						label={"resources.entities.fields.eta_date"}
						headerClassName={cellClasses.cellGreen}
					/>
					<TextField
						source="multi_eta_time"
						label={"resources.entities.fields.eta_time"}
						headerClassName={cellClasses.cellGreen}
						cellClassName={cellClasses.cellEtdTime}
					/> */}
					<DateFormatField
						source="eta_date"
						headerClassName={cellClasses.cellGreen}
					/>
					<TextField
						source="eta_time"
						headerClassName={cellClasses.cellGreen}
						cellClassName={cellClasses.cellEtdTime}
					/>
					<OSFCUserSelectField
						source="assignee_1"
						visibleSource="assignee_1_name"
						headerClassName={cellClasses.cellGreen}
						permissions={permissions}
					/>
					<OutgoingTripNumberOrIsRecurringField
						label={
							<LabelWithTooltip
								tooltip={t("trip_number_or_is_recurring_full")}
								label={t("trip_number_or_is_recurring")}
							/>
						}
						terminalId={globalFilters?.terminal_id?.[0]}
					/>
					{/* TODO: Ask Erik if we need t ouse srat_plan_datetime and deadline_date_time? */}
					{/* <DateFormatField
						source="multi_etd_date"
						label={"resources.entities.fields.etd_date"}
						headerClassName={cellClasses.cellRed}
					/>
					<TextField
						source="multi_etd_time"
						label={"resources.entities.fields.etd_time"}
						headerClassName={cellClasses.cellRed}
						cellClassName={cellClasses.cellEtdTime}
					/> */}
					<DateFormatField
						source="etd_date"
						headerClassName={cellClasses.cellRed}
					/>
					<TextField
						source="etd_time"
						headerClassName={cellClasses.cellRed}
						cellClassName={cellClasses.cellEtdTime}
					/>
					<OSFCUserSelectField
						source="assignee_2"
						visibleSource="assignee_2_name"
						headerClassName={cellClasses.cellRed}
						permissions={permissions}
					/>
					<MatchNotesField label="resources.entities.fields.notes" />
					<MatchCompanyField label="resources.entities.fields.company" />
					<DateTimeField source="created_at" addLabel />
					<DateTimeField source="completed_at" addLabel />
					<MatchStatusField
						permissions={permissions}
						isAppManager={isAppManager}
						isOSFCUser={isOSFCUser}
						label="resources.tasks.fields.status"
					/>
					<MatchServiceField label="resources.tasks.fields.service" />
					<TextField
						label={
							<LabelWithTooltip
								tooltip={translate("osfc.fields.suffix.minutes")}
								label={t("work_time")}
							/>
						}
						source="work_time"
					/>
					<MatchInternalNotesField label="resources.entities.fields.internal_notes" />
				</Datagrid>
			</ScrollingWrapper>
		</AppList>
	);
};

export const EntitiesList = (props) => (
	<GridUserDataProvider
		resource={props.resource}
		filterDefaultValues={filterDefaultValues}
		sortDefaultValues={defaultSort}
	>
		<_EntitiesList {...props} />
	</GridUserDataProvider>
);
